import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  TextField,
  Button,
  IconButton,
  Alert,
  Divider,
  Snackbar
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';

const SINGLE = 'SinglePackage';
const MIX = 'MixAndMatchByGroup';

let _rowSeq = 0;
const rowId = () => 'bg-' + ++_rowSeq;

// Allocation model + benefit groups + danger zone. Persists via the existing
// edit-product dispatch (benefit_allocation / benefit_groups are on ProductDto).
const AllocationSettingsTab = (props) => {
  const product = props.product || {};

  const [allocation, setAllocation] = useState(SINGLE);
  const [groups, setGroups] = useState([]);
  const [justSaved, setJustSaved] = useState(false);
  const [snackbar, setSnackbar] = useState(null); // { severity, message }

  // Reset local state from the server values ONLY when they actually change
  // (value-signature, not object identity) — the parent refetches the product
  // aggressively after unrelated sub-entity saves, which would otherwise clobber
  // in-progress edits here on every render.
  const serverSig = JSON.stringify([
    product.benefit_allocation || SINGLE,
    product.benefit_groups || []
  ]);
  useEffect(() => {
    setAllocation(product.benefit_allocation || SINGLE);
    setGroups(
      (product.benefit_groups || []).map((g) => ({
        _id: rowId(),
        code: g.code || '',
        name: g.name || ''
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.guid, serverSig]);

  // Surface success/error of the allocation save (gated by justSaved so a shared
  // product-edit success from another tab doesn't fire a spurious snackbar here).
  useEffect(() => {
    if (justSaved && props.isSuccess) {
      setSnackbar({ severity: 'success', message: 'Allocation settings saved.' });
      setJustSaved(false);
      if (props.resetSuccess) props.resetSuccess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isSuccess, justSaved]);

  useEffect(() => {
    if (justSaved && props.isError) {
      setSnackbar({
        severity: 'error',
        message:
          'Error saving allocation: ' +
          ((props.isError && props.isError.message) || props.isError || 'please try again')
      });
      setJustSaved(false);
      if (props.resetError) props.resetError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isError, justSaved]);

  const isMix = allocation === MIX;

  const updateGroup = (id, patch) =>
    setGroups((list) => list.map((g) => (g._id === id ? { ...g, ...patch } : g)));
  const removeGroup = (id) => setGroups((list) => list.filter((g) => g._id !== id));
  const addGroup = () =>
    setGroups((list) => list.concat({ _id: rowId(), code: '', name: '' }));

  const handleSave = () => {
    if (props.onSave) {
      setJustSaved(true);
      props.onSave({
        benefit_allocation: allocation,
        benefit_groups: groups
          .filter((g) => g.code.trim())
          .map((g) => ({ code: g.code.trim(), name: g.name.trim() }))
      });
    }
  };

  return (
    <Box>
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1f2937', mb: 2 }}>
            Benefit Allocation Model
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <Select value={allocation} onChange={(e) => setAllocation(e.target.value)}>
              <MenuItem value={SINGLE}>
                Single Package — member picks one package, gets all its benefits
              </MenuItem>
              <MenuItem value={MIX}>
                Mix and Match by Group — member picks one package per benefit group
              </MenuItem>
            </Select>
          </FormControl>

          <Alert severity="info" sx={{ mb: isMix ? 2 : 0 }}>
            {isMix
              ? 'Mix-and-Match: the member picks one package per benefit group below. Package-level premiums are ignored — the total is the sum of each selected benefit’s own premium + add-ons.'
              : 'Single Package: the _package rating node (or static package premium) is the whole premium. Benefit groups below are unused in this mode.'}
          </Alert>

          {isMix && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography
                sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6B7280', mb: 1.5 }}
              >
                Benefit Groups
              </Typography>
              {groups.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  No benefit groups yet.
                </Typography>
              )}
              {groups.map((g) => (
                <Box key={g._id} sx={{ display: 'flex', gap: 1.5, mb: 1.5, alignItems: 'center' }}>
                  <TextField
                    size="small"
                    label="Code"
                    value={g.code}
                    onChange={(e) => updateGroup(g._id, { code: e.target.value })}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    size="small"
                    label="Label"
                    value={g.name}
                    onChange={(e) => updateGroup(g._id, { name: e.target.value })}
                    sx={{ flex: 1 }}
                  />
                  <IconButton size="small" onClick={() => removeGroup(g._id)} sx={{ color: '#c62828' }}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Button
                variant="outlined"
                size="small"
                startIcon={<Add />}
                onClick={addGroup}
                sx={{ textTransform: 'none', color: '#173E4B', borderColor: '#173E4B' }}
              >
                Add Group
              </Button>
            </Box>
          )}

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={props.isLoading}
              sx={{ textTransform: 'none', bgcolor: '#173E4B', '&:hover': { bgcolor: '#0f2d38' } }}
            >
              Save Allocation Settings
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ borderColor: '#FECACA' }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#B91C1C', mb: 1 }}>
            Danger Zone
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 13 }}>Delete this product</Typography>
              <Typography variant="body2" color="text.secondary">
                {props.onDelete
                  ? 'This cannot be undone.'
                  : 'Product deletion is managed from the Products list.'}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="error"
              disabled={!props.onDelete}
              onClick={props.onDelete}
              sx={{ textTransform: 'none' }}
            >
              Delete Product
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {snackbar ? (
          <Alert
            onClose={() => setSnackbar(null)}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default AllocationSettingsTab;
